import {
  Building2,
  Check,
  ChevronsUpDown,
  GraduationCap,
  Loader2,
  Plus,
  ShieldCheck,
  Stethoscope,
  UserCircle,
  Wrench,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import type { FormEvent, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/authStore";
import { cn } from "@/lib/utils/cn";
import {
  useCreateFunctionalAccountMutation,
  useSwitchFunctionalAccountMutation,
} from "@/features/auth/hooks/useAuthMutations";
import type { FunctionalAccount, FunctionalAccountStatus, FunctionalAccountType } from "@/features/auth/types";
import { getFunctionalAccountDisplayName, getFunctionalAccountLabel } from "@/features/auth/utils/functionalAccountLabels";
import { Button, Input, Label } from "@/shared/ui";

const functionalAccountOptions: Array<{ value: FunctionalAccountType; label: string }> = [
  { value: "basic", label: "Basic" },
  { value: "patient", label: "Patient" },
  { value: "doctor", label: "Doctor" },
  { value: "specialist", label: "Specialist" },
  { value: "technician", label: "Technician" },
  { value: "business_owner", label: "Business Owner" },
];

export interface AccountManagementPanelProps {
  compact?: boolean;
  createOnly?: boolean;
  createFormRequestKey?: number;
  requestedFunctionalAccountType?: FunctionalAccountType;
  onClose?: () => void;
  onManageAccounts?: () => void;
}

export const AccountManagementPanel = ({
  compact = false,
  createOnly = false,
  createFormRequestKey,
  requestedFunctionalAccountType,
  onClose,
  onManageAccounts,
}: AccountManagementPanelProps): JSX.Element => {
  const navigate = useNavigate();
  const availableFunctionalAccounts = useAuthStore((state) => state.availableFunctionalAccounts);
  const activeFunctionalAccount = useAuthStore((state) => state.activeFunctionalAccount);
  const createFunctionalAccountMutation = useCreateFunctionalAccountMutation();
  const switchFunctionalAccountMutation = useSwitchFunctionalAccountMutation();
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [functionalAccountType, setFunctionalAccountType] = useState<FunctionalAccountType>("patient");
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    if (createFormRequestKey !== undefined) {
      if (requestedFunctionalAccountType) {
        setFunctionalAccountType(requestedFunctionalAccountType);
      }
      setIsCreateFormOpen(true);
    }
  }, [createFormRequestKey, requestedFunctionalAccountType]);

  const sortedAccounts = useMemo(
    () =>
      [...availableFunctionalAccounts].sort((leftAccount, rightAccount) => {
        if (leftAccount.id === activeFunctionalAccount?.id) return -1;
        if (rightAccount.id === activeFunctionalAccount?.id) return 1;
        return leftAccount.functional_account_type.localeCompare(rightAccount.functional_account_type);
      }),
    [activeFunctionalAccount?.id, availableFunctionalAccounts],
  );

  const createError = createFunctionalAccountMutation.error ? getApiErrorMessage(createFunctionalAccountMutation.error) : null;
  const switchError = switchFunctionalAccountMutation.error ? getApiErrorMessage(switchFunctionalAccountMutation.error) : null;

  const submitCreateFunctionalAccount = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (functionalAccountType === "business_owner") {
      onClose?.();
      navigate("/app/business/register");
      return;
    }

    createFunctionalAccountMutation.mutate(
      {
        functional_account_type: functionalAccountType,
        display_name: displayName.trim() || null,
      },
      {
        onSuccess: () => {
          setDisplayName("");
          setIsCreateFormOpen(false);
          if (createOnly) {
            onClose?.();
          }
        },
      },
    );
  };

  const switchFunctionalAccount = (functionalAccount: FunctionalAccount): void => {
    if (functionalAccount.id === activeFunctionalAccount?.id || switchFunctionalAccountMutation.isPending) {
      return;
    }

    switchFunctionalAccountMutation.mutate(
      { functional_account_id: Number(functionalAccount.id) },
      {
        onSuccess: () => {
          onClose?.();
          navigate(resolveFunctionalAccountLandingPath(functionalAccount.functional_account_type), { replace: true });
        },
      },
    );
  };

  return (
    <section className={cn("min-w-0", compact ? "space-y-3" : "space-y-5")}>
      {!createOnly ? (
        <>
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-card-foreground">My Accounts</p>
            </div>
            {onManageAccounts ? (
              <Button type="button" size="sm" variant="outline" className="shrink-0 rounded-2xl" onClick={onManageAccounts}>
                Manage
              </Button>
            ) : null}
          </div>

          <div className="grid gap-2">
            {sortedAccounts.length > 0 ? (
              sortedAccounts.map((functionalAccount) => {
                const isActive = functionalAccount.id === activeFunctionalAccount?.id;

                return (
                  <button
                    key={functionalAccount.id}
                    type="button"
                    className={cn(
                      "flex w-full min-w-0 items-center gap-3 rounded-2xl border border-border bg-card/70 p-3 text-left transition hover:border-primary/35 hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/30",
                      isActive && "border-primary/45 bg-primary/5",
                    )}
                    onClick={() => switchFunctionalAccount(functionalAccount)}
                    disabled={isActive || switchFunctionalAccountMutation.isPending}
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                      {isActive ? <Check className="h-5 w-5" aria-hidden="true" /> : <UserCircle className="h-5 w-5" aria-hidden="true" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-card-foreground">
                        {functionalAccount.display_name || getFunctionalAccountLabel(functionalAccount.functional_account_type)}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                        <span>{getFunctionalAccountLabel(functionalAccount.functional_account_type)}</span>
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-secondary-foreground">
                          {getStatusLabel(functionalAccount.status)}
                        </span>
                      </span>
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  </button>
                );
              })
            ) : (
              <div className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                Nuk ka functional accounts te sinkronizuara ende.
              </div>
            )}
          </div>

          {switchError ? <p className="text-xs font-medium text-destructive">{switchError}</p> : null}
        </>
      ) : null}

      <div className="rounded-2xl border border-border bg-[hsl(var(--surface))]/65 p-3">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 text-left"
          onClick={() => setIsCreateFormOpen((current) => !current)}
          aria-expanded={isCreateFormOpen}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
              <Plus className="h-4 w-4" aria-hidden="true" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-card-foreground">Create Account</span>
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>

        {isCreateFormOpen ? (
          <form className="mt-3 grid gap-3" onSubmit={submitCreateFunctionalAccount}>
            <div className="grid gap-1.5">
              <Label htmlFor={compact ? "functional-account-type-compact" : "functional-account-type"}>Account type</Label>
              <select
                id={compact ? "functional-account-type-compact" : "functional-account-type"}
                className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground app-focus"
                value={functionalAccountType}
                onChange={(event) => setFunctionalAccountType(event.target.value as FunctionalAccountType)}
              >
                {functionalAccountOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor={compact ? "functional-account-display-name-compact" : "functional-account-display-name"}>
                Display name
              </Label>
              <Input
                id={compact ? "functional-account-display-name-compact" : "functional-account-display-name"}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="P.sh. Profili profesional"
                maxLength={255}
              />
            </div>

            {createError ? <p className="text-xs font-medium text-destructive">{createError}</p> : null}

            <Button type="submit" className="rounded-2xl" disabled={createFunctionalAccountMutation.isPending}>
              {createFunctionalAccountMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Create Account
            </Button>
          </form>
        ) : null}
      </div>

    </section>
  );
};

export interface AccountManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  createFormRequestKey: number;
  requestedFunctionalAccountType: FunctionalAccountType;
}

export const AccountManagementDialog = ({
  open,
  onOpenChange,
  createFormRequestKey,
  requestedFunctionalAccountType,
}: AccountManagementDialogProps): JSX.Element => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-[80] bg-slate-950/45 backdrop-blur-sm" />
      <Dialog.Content className="fixed left-3 right-3 top-20 z-[81] max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-[26px] border border-border bg-card p-4 text-card-foreground shadow-2xl sm:left-1/2 sm:right-auto sm:top-1/2 sm:w-[calc(100vw-1.5rem)] sm:max-w-[520px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Dialog.Title className="text-lg font-bold text-foreground">Shto account</Dialog.Title>
            <Dialog.Description className="sr-only">
              Krijo nje account funksional.
            </Dialog.Description>
          </div>
          <Dialog.Close asChild>
            <Button type="button" variant="ghost" size="icon" className="shrink-0 rounded-full" aria-label="Mbyll">
              <X className="h-5 w-5" aria-hidden="true" />
            </Button>
          </Dialog.Close>
        </div>
        <AccountManagementPanel
          compact
          createOnly
          createFormRequestKey={createFormRequestKey}
          requestedFunctionalAccountType={requestedFunctionalAccountType}
          onClose={() => onOpenChange(false)}
        />
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

interface FloatingAccountSlot {
  id: "basic" | "patient" | "professional" | "technician" | "business";
  label: string;
  createLabel: string;
  createType: FunctionalAccountType;
  account: FunctionalAccount | null;
  icon: LucideIcon;
  letter: string;
}

interface FloatingDockPosition {
  left: number;
  top: number;
}

const floatingDockPositionStorageKey = "eshendetesia:floating-account-dock-position";
const floatingDockMargin = 12;
const floatingDockSize = 56;

const readFloatingDockPosition = (): FloatingDockPosition | null => {
  try {
    const stored = window.localStorage.getItem(floatingDockPositionStorageKey);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<FloatingDockPosition>;
    if (!Number.isFinite(parsed.left) || !Number.isFinite(parsed.top)) return null;
    return { left: Number(parsed.left), top: Number(parsed.top) };
  } catch {
    return null;
  }
};

const clampFloatingDockPosition = (position: FloatingDockPosition): FloatingDockPosition => ({
  left: Math.min(Math.max(floatingDockMargin, position.left), Math.max(floatingDockMargin, window.innerWidth - floatingDockSize - floatingDockMargin)),
  top: Math.min(Math.max(floatingDockMargin, position.top), Math.max(floatingDockMargin, window.innerHeight - floatingDockSize - floatingDockMargin)),
});

export const FloatingAccountDock = (): JSX.Element => {
  const navigate = useNavigate();
  const activeFunctionalAccount = useAuthStore((state) => state.activeFunctionalAccount);
  const availableFunctionalAccounts = useAuthStore((state) => state.availableFunctionalAccounts);
  const switchFunctionalAccountMutation = useSwitchFunctionalAccountMutation();
  const [isDockOpen, setIsDockOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createFormRequestKey, setCreateFormRequestKey] = useState(0);
  const [requestedFunctionalAccountType, setRequestedFunctionalAccountType] = useState<FunctionalAccountType>("patient");
  const [dockPosition, setDockPosition] = useState<FloatingDockPosition | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ pointerId: number; offsetX: number; offsetY: number; moved: boolean } | null>(null);
  const ignoreNextDockClickRef = useRef(false);

  const accountByType = useMemo(() => {
    const map = new Map<FunctionalAccountType, FunctionalAccount>();

    for (const functionalAccount of availableFunctionalAccounts) {
      if (!map.has(functionalAccount.functional_account_type)) {
        map.set(functionalAccount.functional_account_type, functionalAccount);
      }
    }

    return map;
  }, [availableFunctionalAccounts]);

  const professionalAccount = accountByType.get("doctor") ?? accountByType.get("specialist") ?? null;
  const hasNonBasicFunctionalAccount = availableFunctionalAccounts.some(
    (functionalAccount) => functionalAccount.functional_account_type !== "basic",
  );
  const activeSlotLabel = activeFunctionalAccount
    ? getFloatingAccountLabel(activeFunctionalAccount.functional_account_type)
    : "Llogaria";
  const activeSlotLetter = getFloatingAccountLetter(activeFunctionalAccount?.functional_account_type ?? null);
  const ActiveSlotIcon = getFloatingAccountIcon(activeFunctionalAccount?.functional_account_type ?? null);

  const slots: FloatingAccountSlot[] = [
    {
      id: "basic",
      label: "Basic",
      createLabel: "Krijo Basic",
      createType: "basic",
      account: accountByType.get("basic") ?? null,
      icon: ShieldCheck,
      letter: "B",
    },
    {
      id: "patient",
      label: "Pacient",
      createLabel: "Krijo Pacient",
      createType: "patient",
      account: accountByType.get("patient") ?? null,
      icon: UserCircle,
      letter: "P",
    },
    {
      id: "professional",
      label: professionalAccount ? getFloatingAccountLabel(professionalAccount.functional_account_type) : "Profesional",
      createLabel: "Krijo Profesional",
      createType: "doctor",
      account: professionalAccount,
      icon: Stethoscope,
      letter: professionalAccount?.functional_account_type === "specialist" ? "S" : "D",
    },
    {
      id: "technician",
      label: "Teknik",
      createLabel: "Krijo Teknik",
      createType: "technician",
      account: accountByType.get("technician") ?? null,
      icon: Wrench,
      letter: "T",
    },
    {
      id: "business",
      label: "Biznes",
      createLabel: "Krijo Biznes",
      createType: "business_owner",
      account: accountByType.get("business_owner") ?? null,
      icon: Building2,
      letter: "K",
    },
  ];
  const visibleSlots = hasNonBasicFunctionalAccount
    ? slots.filter((slot) => slot.id !== "basic")
    : slots;

  useEffect(() => {
    setDockPosition(readFloatingDockPosition());
  }, []);

  useEffect(() => {
    const keepDockInViewport = (): void => {
      setDockPosition((current) => (current ? clampFloatingDockPosition(current) : current));
    };

    window.addEventListener("resize", keepDockInViewport);
    return () => window.removeEventListener("resize", keepDockInViewport);
  }, []);

  useEffect(() => {
    if (!isDockOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent): void => {
      if (dockRef.current && !dockRef.current.contains(event.target as Node)) {
        setIsDockOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsDockOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDockOpen]);

  const switchFunctionalAccountFromDock = (functionalAccount: FunctionalAccount): void => {
    if (functionalAccount.id === activeFunctionalAccount?.id || switchFunctionalAccountMutation.isPending) {
      setIsDockOpen(false);
      return;
    }

    switchFunctionalAccountMutation.mutate(
      { functional_account_id: Number(functionalAccount.id) },
      {
        onSuccess: () => {
          setIsDockOpen(false);
          navigate(resolveFunctionalAccountLandingPath(functionalAccount.functional_account_type), { replace: true });
        },
      },
    );
  };

  const requestAccountCreation = (functionalAccountType: FunctionalAccountType): void => {
    setIsDockOpen(false);
    if (functionalAccountType === "business_owner") {
      navigate("/app/business/register");
      return;
    }

    setRequestedFunctionalAccountType(functionalAccountType);
    setCreateFormRequestKey((current) => current + 1);
    setIsCreateDialogOpen(true);
  };

  const handleDockPointerDown = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    if (event.button !== 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    dragStateRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDockPointerMove = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    const nextPosition = clampFloatingDockPosition({
      left: event.clientX - dragState.offsetX,
      top: event.clientY - dragState.offsetY,
    });
    const moved = Math.abs(nextPosition.left - event.currentTarget.getBoundingClientRect().left) > 3
      || Math.abs(nextPosition.top - event.currentTarget.getBoundingClientRect().top) > 3;
    dragState.moved ||= moved;
    if (dragState.moved) {
      setDockPosition(nextPosition);
    }
  };

  const handleDockPointerUp = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    dragStateRef.current = null;
    if (!dragState.moved) return;
    ignoreNextDockClickRef.current = true;
    const nextPosition = clampFloatingDockPosition({
      left: event.clientX - dragState.offsetX,
      top: event.clientY - dragState.offsetY,
    });
    setDockPosition(nextPosition);
    window.localStorage.setItem(floatingDockPositionStorageKey, JSON.stringify(nextPosition));
  };

  return (
    <>
      <div
        ref={dockRef}
        className="fixed bottom-5 right-4 z-[58] flex flex-col-reverse items-end gap-2 md:bottom-auto md:right-5 md:top-1/2 md:-translate-y-1/2 md:flex-col"
        style={dockPosition ? { left: dockPosition.left, top: dockPosition.top, right: "auto", bottom: "auto", transform: "none" } : undefined}
      >
        {isDockOpen ? (
          <div className="flex flex-col-reverse items-end gap-2 md:flex-col">
            {visibleSlots.map((slot) => {
              const isActive = slot.account?.id === activeFunctionalAccount?.id;
              const SlotIcon = slot.icon;

              return (
                <div key={slot.id} className="group relative flex items-center justify-end">
                  <span className="pointer-events-none absolute right-14 max-w-44 translate-x-2 whitespace-nowrap rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-card-foreground opacity-0 shadow-lg transition group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:translate-x-0 group-focus-within:opacity-100">
                    {slot.account ? slot.label : slot.createLabel}
                  </span>
                  <button
                    type="button"
                    className={cn(
                      "grid h-11 w-11 place-items-center rounded-full border bg-card text-card-foreground shadow-xl transition hover:-translate-y-0.5 hover:border-primary/45 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/35",
                      isActive && "border-primary bg-primary text-primary-foreground ring-4 ring-primary/15 hover:text-primary-foreground",
                      !slot.account && "border-dashed text-primary",
                    )}
                    aria-label={slot.account ? `Kalo te ${slot.label}` : slot.createLabel}
                    aria-current={isActive ? "true" : undefined}
                    disabled={switchFunctionalAccountMutation.isPending && Boolean(slot.account)}
                    onClick={() => {
                      if (slot.account) {
                        switchFunctionalAccountFromDock(slot.account);
                        return;
                      }
                      requestAccountCreation(slot.createType);
                    }}
                  >
                    {slot.account ? (
                      <span className="relative grid h-full w-full place-items-center">
                        <SlotIcon className="h-5 w-5" aria-hidden="true" />
                        <span className="absolute bottom-1 right-1 text-[9px] font-black leading-none">{slot.letter}</span>
                      </span>
                    ) : (
                      <Plus className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        <div className="group relative flex items-center justify-end">
          <span className="pointer-events-none absolute right-16 max-w-48 translate-x-2 whitespace-nowrap rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-card-foreground opacity-0 shadow-lg transition group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:translate-x-0 group-focus-within:opacity-100">
            {activeSlotLabel}
          </span>
          <button
            type="button"
            className="grid h-14 w-14 touch-none place-items-center rounded-full border border-primary/30 bg-primary text-primary-foreground shadow-2xl shadow-primary/25 transition hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-primary/30"
            aria-label="Hap account dock"
            aria-expanded={isDockOpen}
            onPointerDown={handleDockPointerDown}
            onPointerMove={handleDockPointerMove}
            onPointerUp={handleDockPointerUp}
            onPointerCancel={() => { dragStateRef.current = null; }}
            onClick={() => {
              if (ignoreNextDockClickRef.current) {
                ignoreNextDockClickRef.current = false;
                return;
              }
              setIsDockOpen((current) => !current);
            }}
          >
            <span className="relative grid h-full w-full place-items-center">
              <ActiveSlotIcon className="h-6 w-6" aria-hidden="true" />
              <span className="absolute bottom-2 right-2 text-[10px] font-black leading-none">{activeSlotLetter}</span>
            </span>
          </button>
        </div>
      </div>

      <AccountManagementDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        createFormRequestKey={createFormRequestKey}
        requestedFunctionalAccountType={requestedFunctionalAccountType}
      />
    </>
  );
};

const getFloatingAccountIcon = (functionalAccountType: FunctionalAccountType | null): LucideIcon => {
  if (functionalAccountType === "business_owner") return Building2;
  if (functionalAccountType === "doctor" || functionalAccountType === "specialist") return Stethoscope;
  if (functionalAccountType === "technician") return Wrench;
  if (functionalAccountType === "patient") return UserCircle;
  if (functionalAccountType === null) return GraduationCap;
  return ShieldCheck;
};

const getFloatingAccountLetter = (functionalAccountType: FunctionalAccountType | null): string => {
  if (functionalAccountType === "business_owner") return "K";
  if (functionalAccountType === "specialist") return "S";
  if (functionalAccountType === "doctor") return "D";
  if (functionalAccountType === "technician") return "T";
  if (functionalAccountType === "patient") return "P";
  if (functionalAccountType === "basic") return "B";
  return "A";
};

const getFloatingAccountLabel = (functionalAccountType: FunctionalAccountType): string => {
  if (functionalAccountType === "business_owner") return "Biznes";
  if (functionalAccountType === "specialist") return "Specialist";
  if (functionalAccountType === "doctor") return "Doktor";
  if (functionalAccountType === "technician") return "Teknik";
  if (functionalAccountType === "patient") return "Pacient";
  return "Basic";
};

export const AccountSwitcherInline = (): JSX.Element => {
  const navigate = useNavigate();
  const activeFunctionalAccount = useAuthStore((state) => state.activeFunctionalAccount);
  const availableFunctionalAccounts = useAuthStore((state) => state.availableFunctionalAccounts);
  const switchFunctionalAccountMutation = useSwitchFunctionalAccountMutation();
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const activeLabel = activeFunctionalAccount
    ? getFunctionalAccountDisplayName(activeFunctionalAccount)
    : "Shto account";

  useEffect(() => {
    if (!isSwitcherOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent): void => {
      if (switcherRef.current && !switcherRef.current.contains(event.target as Node)) {
        setIsSwitcherOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsSwitcherOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSwitcherOpen]);

  const switchFunctionalAccount = (functionalAccount: FunctionalAccount): void => {
    if (functionalAccount.id === activeFunctionalAccount?.id || switchFunctionalAccountMutation.isPending) {
      return;
    }

    switchFunctionalAccountMutation.mutate(
      { functional_account_id: Number(functionalAccount.id) },
      {
        onSuccess: () => {
          setIsSwitcherOpen(false);
          navigate(resolveFunctionalAccountLandingPath(functionalAccount.functional_account_type), { replace: true });
        },
      },
    );
  };

  return (
    <div ref={switcherRef} className="relative min-w-0">
      <button
        type="button"
        className="inline-flex h-10 max-w-[9.5rem] min-w-0 items-center gap-2 rounded-full border border-border bg-card px-3 text-sm font-semibold text-card-foreground shadow-sm transition hover:border-primary/35 hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 sm:max-w-64"
        aria-haspopup="menu"
        aria-expanded={isSwitcherOpen}
        onClick={() => setIsSwitcherOpen((current) => !current)}
      >
        <ShieldCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="truncate">{activeLabel}</span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      {isSwitcherOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[64] cursor-default bg-background/65 backdrop-blur-sm md:bg-transparent md:backdrop-blur-0"
            aria-label="Mbyll account switcher"
            onClick={() => setIsSwitcherOpen(false)}
          />
          <div
            className="fixed left-3 right-3 top-20 z-[72] max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-[22px] border border-border bg-card text-card-foreground shadow-2xl md:absolute md:left-0 md:right-auto md:top-12 md:bottom-auto md:max-h-none md:w-[min(20rem,calc(100vw-1.5rem))] md:overflow-hidden"
            role="menu"
          >
            <div className="grid gap-1 p-2">
              {availableFunctionalAccounts.length > 0 ? (
                availableFunctionalAccounts.map((functionalAccount) => {
                  const isActive = functionalAccount.id === activeFunctionalAccount?.id;
                  const Icon = getFunctionalAccountIcon(functionalAccount.functional_account_type);

                  return (
                    <button
                      key={functionalAccount.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={isActive}
                      className={cn(
                        "flex w-full min-w-0 items-center gap-3 rounded-2xl px-3 py-3 text-left transition hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/30",
                        isActive && "bg-primary/5",
                      )}
                      disabled={isActive || switchFunctionalAccountMutation.isPending}
                      onClick={() => switchFunctionalAccount(functionalAccount)}
                    >
                      <Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                          {getFunctionalAccountDisplayName(functionalAccount)}
                        </span>
                      </span>
                      {isActive ? (
                        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                          Aktiv
                        </span>
                      ) : null}
                    </button>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
                  Nuk ka account aktiv.
                </div>
              )}
            </div>

            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-3 border-t border-border px-5 py-4 text-left text-sm font-semibold text-primary transition hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/30"
              onClick={() => {
                setIsSwitcherOpen(false);
                navigate("/app/dashboard");
              }}
            >
              <Plus className="h-5 w-5" aria-hidden="true" />
              Shto account te ri
            </button>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-3 border-t border-border px-5 py-4 text-left text-sm font-semibold text-primary transition hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/30"
              onClick={() => {
                setIsSwitcherOpen(false);
                navigate("/app/business/register");
              }}
            >
              <Building2 className="h-5 w-5" aria-hidden="true" />
              Shto Business Account
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

const getFunctionalAccountIcon = (functionalAccountType: FunctionalAccountType): LucideIcon => {
  if (functionalAccountType === "business_owner") {
    return Building2;
  }

  if (functionalAccountType === "basic") {
    return ShieldCheck;
  }

  return UserCircle;
};

const resolveFunctionalAccountLandingPath = (functionalAccountType: FunctionalAccountType): string => {
  const landingPaths: Record<FunctionalAccountType, string> = {
    basic: "/app/dashboard",
    patient: "/app/patient",
    doctor: "/app/professional/settings",
    specialist: "/app/professional/settings",
    technician: "/app/laboratory",
    business_owner: "/app/business/manage",
  };

  return landingPaths[functionalAccountType] ?? "/app/dashboard";
};

const getStatusLabel = (status: FunctionalAccountStatus): string => {
  const labels: Record<FunctionalAccountStatus, string> = {
    draft: "Draft",
    pending_verification: "Pending Verification",
    verified: "Verified",
    rejected: "Rejected",
    disabled: "Disabled",
  };

  return labels[status];
};
